import { nextTick, ref, watch, type Ref } from 'vue'

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ')

export function useDialogA11y(open: Ref<boolean>, onClose: () => void) {
  const dialogRef = ref<HTMLElement | null>(null)
  let previousFocusedElement: HTMLElement | null = null

  const onDialogKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (event.key !== 'Tab' || !dialogRef.value) {
      return
    }

    const focusable = Array.from(
      dialogRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((element) => element.getAttribute('aria-hidden') !== 'true')

    if (focusable.length === 0) {
      event.preventDefault()
      dialogRef.value.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (!event.shiftKey && active === last) {
      event.preventDefault()
      first?.focus()
      return
    }

    if (event.shiftKey && (active === first || active === dialogRef.value)) {
      event.preventDefault()
      last?.focus()
    }
  }

  watch(open, async (isOpen) => {
    if (isOpen) {
      previousFocusedElement =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
      await nextTick()
      dialogRef.value?.focus()
      return
    }

    previousFocusedElement?.focus()
    previousFocusedElement = null
  })

  return {
    dialogRef,
    onDialogKeydown
  }
}
